import "./settings.css";
import React, { useContext, useEffect, useState } from "react";
import logo from "../../assets/logo.png";
import settingsIcon from "../../assets/settings.png";
import helpIcon from "../../assets/help.png";
import show from "../../assets/show.png";
import hide from "../../assets/hide.png";
import logoutIcon from "../../assets/logout.png";
import cardIcon from "../../assets/card.png";
import Hamburger from "hamburger-react";
import { AuthContext } from "../../context/auth-context";
import { Link, useNavigate } from "react-router-dom";
import { request } from "../../utils/axios-utils";
import trashIcon from "../../assets/trash.png";
import visaLogo from "../../assets/cardLogos/visa.jpeg";
import mastercardLogo from "../../assets/cardLogos/mastercard.jpeg";
import amexLogo from "../../assets/cardLogos/amex.png";
import unknownCardLogo from "../../assets/cardLogos/unknown_card_logo.png";
import { ClipLoader } from "react-spinners";
import Modal from "../../components/Modal/Modal";
import { toast } from "react-hot-toast";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import poweredByStripe from "../../assets/powered_by_stripe.png";

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#a86638",
      fontWeight: 400,
    },
  },
};

const getCardBrandLogo = (brand) => {
  const brandLogoMap = {
    visa: visaLogo,
    mastercard: mastercardLogo,
    amex: amexLogo,
  };

  return brandLogoMap[brand?.toLowerCase()] || unknownCardLogo;
};

const Settings = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [settingsData, setSettingsData] = useState({});
  const [activeModal, setActiveModal] = useState("");

  const [oldPassword, setOldPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const stripe = useStripe();
  const elements = useElements();
  const [cardholderName, setCardholderName] = useState("");
  const [deleteModal, setDeleteModal] = useState({ type: "", id: null });
  const [password, setPassword] = useState("");

  const changePassword = async () => {
    setError("");
    if (oldPassword === "") {
      setError("Please enter the old password.");
      return;
    } else if (newPassword === "") {
      setError("Please enter the new password.");
      return;
    } else if (newPassword.length < 8) {
      setError("New password must be at least 8 characters long.");
      return;
    } else if (confirmNewPassword === "") {
      setError("Please confirm your new password.");
      return;
    } else if (newPassword !== confirmNewPassword) {
      setError("Passwords don't match.");
      return;
    }

    try {
      setLoading("changePassword");

      const response = await request({
        url: "/member/change-password",
        method: "POST",
        data: { oldPassword, newPassword },
      });

      toast.success(response.data.message);
      setActiveModal("");
      setOldPassword("");
      setShowOldPassword(false);
      setNewPassword("");
      setShowNewPassword(false);
      setConfirmNewPassword("");
      setShowConfirmNewPassword(false);
    } catch (error) {
      setError(error.response.data.message);
    } finally {
      setLoading("");
    }
  };

  const addPaymentMethod = async () => {
    if (cardholderName === "") {
      setError("Please enter Cardholder Name.");
      return;
    }

    try {
      setLoading("addPaymentMethod");
      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);

      const paymentMethod = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          exp_month: cardExpiryElement._current?.value.split("/")[0],
          exp_year: cardExpiryElement._current?.value.split("/")[1],
          cvc: cardCvcElement._current?.value,
          email: settingsData.email,
          name: cardholderName || "",
        },
      });

      const response = await request({
        url: "/member/add-payment-method",
        method: "POST",
        data: {
          paymentMethod: paymentMethod.paymentMethod.id,
        },
      });

      setSettingsData((prevData) => ({
        ...prevData,
        paymentMethods: [
          ...prevData?.paymentMethods,
          response.data.attachedPaymentMethod.card,
        ],
      }));

      toast.success(response.data.message);
      setActiveModal("");
      setCardholderName("");
    } catch (error) {
      setError(error.response.data.message);
    } finally {
      setLoading("");
    }
  };

  const deletePaymentMethod = async () => {
    try {
      const response = await request({
        url: "/member/remove-payment-method",
        method: "POST",
        data: {
          paymentMethod: deleteModal.id,
        },
      });

      if (settingsData && settingsData.paymentMethods) {
        const updatedPaymentMethods = settingsData.paymentMethods.filter(
          (paymentMethod) => paymentMethod.id !== deleteModal.id
        );

        setSettingsData({
          ...settingsData,
          paymentMethods: updatedPaymentMethods,
        });
      }

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };

  const deleteAccount = async () => {
    try {
      setLoading("deleteAccount");

      const response = await request({
        url: "/member/delete-account",
        method: "POST",
        data: { password },
      });

      toast.success(response.data.message);

      logout();
      navigate("/login");
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
      setPassword("");
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading("data");

        const settingsResponse = await request({
          url: "/member/get-settings-data",
        });
        setSettingsData(settingsResponse.data);
      } catch (error) {
      } finally {
        setLoading("");
      }
    };

    getData();
  }, []);

  return (
    <>
      {activeModal === "changePassword" && (
        <Modal
          heading={"Change password"}
          paragraph={
            "Change your password by confirm the existing one and adding a new one."
          }
          closeModal={() => {
            setError("");
            setActiveModal("");
            setOldPassword("");
            setShowOldPassword(false);
            setNewPassword("");
            setShowNewPassword(false);
            setConfirmNewPassword("");
            setShowConfirmNewPassword(false);
          }}
          isLoading={loading === "changePassword"}
          btnText="Change"
          btnFuntion={loading === "changePassword" ? null : changePassword}
        >
          <div className="password-input-div mb-16">
            <input
              value={oldPassword}
              onChange={(e) => {
                setOldPassword(e.target.value);
              }}
              className="regular-input"
              placeholder="Old Password"
              type={showOldPassword ? "text" : "password"}
            />
            <img
              onClick={() => {
                setShowOldPassword((prevState) => !prevState);
              }}
              className="password-input-icon"
              src={showOldPassword ? hide : show}
            />
          </div>

          <div className="password-input-div mb-16">
            <input
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
              }}
              className="regular-input"
              placeholder="New Password"
              type={showNewPassword ? "text" : "password"}
            />
            <img
              onClick={() => {
                setShowNewPassword((prevState) => !prevState);
              }}
              className="password-input-icon"
              src={showNewPassword ? hide : show}
            />
          </div>

          <div className="password-input-div">
            <input
              value={confirmNewPassword}
              onChange={(e) => {
                setConfirmNewPassword(e.target.value);
              }}
              className="regular-input"
              placeholder="Confirm New Password"
              type={showConfirmNewPassword ? "text" : "password"}
            />
            <img
              onClick={() => {
                setShowConfirmNewPassword((prevState) => !prevState);
              }}
              className="password-input-icon"
              src={showConfirmNewPassword ? hide : show}
            />
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {activeModal === "addPaymentMethod" && (
        <Modal
          heading={"Add Payment Method"}
          paragraph={"Add payment method to your account."}
          closeModal={() => {
            setError("");
            setActiveModal("");
            setCardholderName("");
          }}
          isLoading={loading === "addPaymentMethod"}
          btnText="Add"
          btnFuntion={loading === "addPaymentMethod" ? null : addPaymentMethod}
        >
          <input
            name="cardholderName"
            value={cardholderName}
            onChange={(e) => {
              setCardholderName(e.target.value);
            }}
            className="regular-input mb-16"
            placeholder="Cardholder Name"
            type="text"
          />

          <CardNumberElement
            className="regular-input mb-16"
            options={cardElementOptions}
          />

          <div className="settings-card-details-div">
            <CardExpiryElement
              options={cardElementOptions}
              className="regular-input-48"
            />

            <CardCvcElement
              options={cardElementOptions}
              className="regular-input-48"
            />
          </div>

          <div className="powered-by-stripe-div">
            <a href="https://stripe.com/" target="_blank">
              <img className="powered-by-stripe-img" src={poweredByStripe} />
            </a>
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {deleteModal.type === "paymentMethod" && (
        <DeleteModal
          heading={"Remove Payment Method?"}
          paragraph={"Are you sure you want to remove this payment method?"}
          deleteFunction={deletePaymentMethod}
          setDeleteModal={setDeleteModal}
          btnText={"Remove"}
        />
      )}

      {deleteModal.type === "deleteAccount" && (
        <DeleteModal
          heading={"Delete Account?"}
          paragraph={"This will delete all menus and related data."}
          deleteFunction={loading === "deleteAccount" ? null : deleteAccount}
          setDeleteModal={setDeleteModal}
          btnText="Delete"
        >
          <input
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            type={"password"}
            placeholder="Your password"
            className="classic-input"
          />
        </DeleteModal>
      )}

      <div className="header-container">
        <div className="header-container-bar-div">
          <Link to={"/"}>
            <img src={logo} className="header-logo-img" />
          </Link>

          <div className="header-text-div">
            <div className="header-help-div">Help</div>
            <Link to={"/settings"} className="header-settings-div">
              <img className="header-settings-img" src={settingsIcon} />
            </Link>
            <div className="header-name-div">
              {loading === "data" ? (
                <ClipLoader size={15} color="#fff" />
              ) : (
                <>
                  {settingsData.name + " " + settingsData.surname}
                  <div onClick={logout} className="logout-div">
                    <p>Log out</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="header-hamburger-icon">
            <Hamburger
              toggled={isOpen}
              toggle={() => {
                setIsOpen((prevState) => !prevState);
              }}
              duration={0.5}
              color="#ed6e12"
            />
          </div>
        </div>

        <div
          className={`header-mobile-container ${
            isOpen === true ? "header-mobile-container-opened" : ""
          }`}
        >
          <div className="header-mobile-link-bar">
            <img src={helpIcon} className="header-mobile-link-img" />
            <p className="header-mobile-link-p">Help</p>
          </div>
          <Link
            to={"/settings"}
            className="header-mobile-link-bar text-decoration-none"
          >
            <img src={settingsIcon} className="header-mobile-link-img" />
            <p className="header-mobile-link-p">Settings</p>
          </Link>
          <div onClick={logout} className="header-mobile-link-bar">
            <img src={logoutIcon} className="header-mobile-link-img" />
            <p className="header-mobile-link-p">Logout</p>
          </div>
        </div>
      </div>

      <div className="settings-container mt-70">
        <div className="settings-div">
          <h1 className="settings-heading">Settings</h1>

          {loading === "data" ? (
            <ClipLoader size={40} color="#ed6e12" />
          ) : (
            <>
              <h2 className="settings-subheading">Account Settings</h2>

              <h3 className="settings-heading3 mb-8">Name & Surname</h3>
              <p className="settings-property-p">
                {settingsData.name} {settingsData.surname}
              </p>

              <h3 className="settings-heading3 mb-8">Email address</h3>
              <p className="settings-property-p">{settingsData.email}</p>

              <div className="display-flex-space-between">
                <div>
                  <h3 className="settings-heading3 mb-8">Password</h3>
                  <p className="settings-property-p">**********</p>
                </div>
                <div
                  onClick={() => {
                    setActiveModal("changePassword");
                  }}
                  className="settings-heading-btn"
                >
                  Change
                </div>
              </div>

              {/* <h2 className="settings-subheading">Payment Settings</h2>
              <div className="display-flex-space-between mb-16">
                <h3 className="settings-heading3">Payment Methods</h3>
                <div
                  onClick={() => {
                    setActiveModal("addPaymentMethod");
                  }}
                  className="settings-heading-btn"
                >
                  Add
                </div>
              </div>

              {settingsData?.paymentMethods?.length > 0 ? (
                <>
                  {settingsData?.paymentMethods?.map((paymentMethod) => {
                    return (
                      <div
                        key={paymentMethod.id}
                        className="settings-payment-method-bar mb-8"
                      >
                        <div className="settings-payment-method-helper">
                          <img
                            className="settings-payment-method-img"
                            src={getCardBrandLogo(paymentMethod.brand)}
                          />
                          <div>
                            <p className="settings-payment-method-brand-p">
                              {paymentMethod?.brand?.toUpperCase()}
                            </p>
                            <p className="settings-payment-method-number-p">
                              Ending in {paymentMethod?.last4}
                            </p>
                          </div>
                        </div>

                        <div
                          onClick={() => {
                            setDeleteModal({
                              type: "paymentMethod",
                              id: paymentMethod.id,
                            });
                          }}
                          className="settings-payment-method-delete-div"
                        >
                          <img
                            className="settings-payment-method-delete-img"
                            src={trashIcon}
                          />
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <>
                  <img className="settings-no-payment-img" src={cardIcon} />
                  <p className="settings-no-payment-p">No payment methods</p>
                </>
              )} */}

              <div className="settings-danger-line" />

              <div onClick={logout} className="settings-logout-btn mb-16">
                Log out
              </div>

              <div
                onClick={() => {
                  setDeleteModal({
                    type: "deleteAccount",
                    id: null,
                  });
                }}
                className="settings-delete-acc-btn"
              >
                Delete Account
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Settings;
